
import { QuizQuestion, QuizResult } from "@/types";

export const calculateQuizResult = (questions: QuizQuestion[], answers: Record<string, string>): QuizResult => {
  // Calculate the total score
  let totalScore = 0;
  let maxPossibleScore = 0;
  
  questions.forEach(question => {
    // Find max possible score for this question
    const maxQuestionScore = Math.max(...question.options.map(option => option.score));
    maxPossibleScore += maxQuestionScore;
    
    // Calculate user's score for this question
    const answerId = answers[question.id];
    if (answerId) {
      const selectedOption = question.options.find(option => option.id === answerId);
      if (selectedOption) {
        totalScore += selectedOption.score;
      }
    }
  });

  // Calculate percentage score
  const percentageScore = (totalScore / maxPossibleScore) * 100;
  
  // Determine maturity level based on score
  let level = '';
  if (percentageScore < 30) {
    level = 'Early Stage';
  } else if (percentageScore < 60) {
    level = 'Developing';
  } else if (percentageScore < 80) {
    level = 'Established';
  } else {
    level = 'Leading';
  }

  // Determine strengths and areas for improvement
  const strengths: string[] = [];
  const improvements: string[] = [];

  // This is a simplified version - in a real app, you'd have more sophisticated logic
  // based on specific question responses
  if (hasHighScoreFor(questions, answers, [1, 5])) {
    strengths.push('AI oversight structure');
  } else {
    improvements.push('Establish a dedicated AI oversight committee or responsible individual');
  }

  if (hasHighScoreFor(questions, answers, [2, 3])) {
    strengths.push('Regular risk assessment');
  } else {
    improvements.push('Increase frequency of AI risk reviews and regulatory updates');
  }

  if (hasHighScoreFor(questions, answers, [4, 6])) {
    strengths.push('Access to AI expertise');
  } else {
    improvements.push('Enhance board\'s AI expertise through training or external advisors');
  }

  if (hasHighScoreFor(questions, answers, [7, 8])) {
    strengths.push('Ethical review process');
  } else {
    improvements.push('Implement a formal ethical review process for AI initiatives');
  }

  if (hasHighScoreFor(questions, answers, [9, 10])) {
    strengths.push('Confidence in AI governance');
  } else {
    improvements.push('Develop comprehensive AI governance policies and vendor assessment');
  }

  return {
    score: Math.round(percentageScore),
    percentage: Math.round(percentageScore),
    level,
    readinessTier: level,
    strengths: strengths.length > 0 ? strengths : ['Further assessment needed to identify strengths'],
    improvements: improvements.length > 0 ? improvements : ['Comprehensive AI governance program recommended'],
  };
};

// Helper function to check if user scored highly on specific questions
const hasHighScoreFor = (questions: QuizQuestion[], answers: Record<string, string>, questionIds: number[]): boolean => {
  let totalScore = 0;
  let maxPossibleScore = 0;
  
  questionIds.forEach(id => {
    const question = questions.find(q => q.id === id);
    if (question) {
      // Find max possible score for this question
      const maxQuestionScore = Math.max(...question.options.map(option => option.score));
      maxPossibleScore += maxQuestionScore;
      
      // Calculate user's score for this question
      const answerId = answers[id];
      if (answerId) {
        const selectedOption = question.options.find(option => option.id === answerId);
        if (selectedOption) {
          totalScore += selectedOption.score;
        }
      }
    }
  });
  
  // Consider it a strength if score is at least 70% of possible
  return totalScore >= (maxPossibleScore * 0.7);
};

// Add the calculateResults function for backward compatibility with the QuizPage component
export const calculateResults = (answers: Record<string, string>): QuizResult => {
  return calculateQuizResult(quizQuestions, answers);
};

// Import the quiz questions
import { quizQuestions } from "@/data/quizQuestions";
